/* eslint-disable max-len */
/* eslint-disable prefer-destructuring */
import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import { useAppSelector } from '../../../Redux/Store/hooks';

import { CatalogObjectVariantsOption } from '../../Data/CatalogObjectVariantsOption';
import { CatalogObjectVariantsInfo } from '../../Data/CatalogObjectVariants';
import { POSCatalogData, POSCatalogModelData } from '../../../common/libraries/modelViewer/Data/POSCatalogData';
import { CatalogObjectControllerData } from '../../Data/CatalogObjectControllerData';

import { ObjectView } from '../Views/ObjectView';
import {
  SLTXFormatRootFileVariant,
} from '../../../common/libraries/fileFormats/sltx/sltx';
import { CatalogFileCollection } from '../../../common/libraries/fileFormats/catalog/catalog';
import { MeasuresItemData } from '../../../common/libraries/fileFormats/measuresItemData';
import { TreeOptionSelection, TreeOptionSelections } from '../../../common/libraries/modelConfiguration/data/optionsSelection';
import { ModelRuleData } from '../../../common/libraries/modelConfiguration/rules/modelRuleData';
import { Config } from '../../../Resources/Config';
import { CustomAttributeValue, CustomAttributeValues } from '../../../common/libraries/customAttributes/customAttributeValue';

type ObjectControllerParameters = {
  catalog : POSCatalogData,
  collections: CatalogFileCollection[],
  configurations : Config,
};

export function ObjectController(
  {
    catalog,
    collections,
    configurations,
  } : ObjectControllerParameters,
) {
  // From the store, get the strings
  const strings = useAppSelector((state) => {
    return state.TextsReducer.strings;
  })!;

  // From the store, get the collection name
  const collectionName = useAppSelector((state) => {
    return state.NavigationReducer.collection;
  });

  // From the store, get the object name
  const objectName = useAppSelector((state) => {
    return state.NavigationReducer.object;
  });

  const [
    catalogObjectControllerData,
    setCatalogObjectControllerData] = useState<CatalogObjectControllerData | undefined>();

  // const [
  //   variants,
  //   setVariants] = useState<SLTXFormatRootFileVariant[]>([]);

  const [
    measures,
    setMeasures] = useState<MeasuresItemData[]>([]);

  // const [
  //   modelIllumination,
  //   setModelIllumination,
  // ] = useState<ModelIlluminationData>(DefaultModelIlluminationData.get());

  const [selectedPartID/* , setSelectedPartID */] = useState<string | undefined>();
  // const [setSelectedPartCustomizations] = useState<Customization[]>([]);

  const [currentOptionSelection, setCurrentOptionSelection] = useState<TreeOptionSelection[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentCustomAttrubutesValues, setCurrentCustomAttrubutesValues] = useState<CustomAttributeValue[]>([]);

  const findCollectionByName = (
    collectionNameToFind : string,
  ) => {
    return collections.find(
      (collection) => collection.name === collectionNameToFind,
    );
  };

  const optionsConstructor = (
    variant : SLTXFormatRootFileVariant,
  ) : CatalogObjectVariantsOption => {

    const result = new CatalogObjectVariantsOption();
    result.name = variant.name;
    result.thumbnailRef = variant.thumbnailRef;
    result.imageRef = variant.imageRef;
    return result;
  };

  useEffect(() => {

    // Find the collection
    const collection = findCollectionByName(collectionName)!;

    // Find the catalog object
    const catalogObject = catalog.getModelByCollectionIDAndModelName(collection.id, objectName)!;

    // if (catalogObject.catalogData && catalogObject.catalogData.illumination) {
    //   setModelIllumination(catalogObject.catalogData.illumination);
    // }

    // setVariants(catalogObject.getVariants());

    if (catalogObject.catalogData && catalogObject.catalogData?.measures) {
      setMeasures(catalogObject.catalogData?.measures);
    }

    // Get the first variant (if available)
    let firstVariant : SLTXFormatRootFileVariant | undefined;

    if (catalogObject.hasVariants()) {

      const arrVariants = catalogObject.getVariants();
      firstVariant = arrVariants[0];
    }

    // Build an entity to represent the object for this controller to consume
    const catalogObjectInfo = new CatalogObjectControllerData(catalogObject, firstVariant);

    // Build an entity to represent the object variants for this controller to consume
    const catalogObjectVariantsInfo = new CatalogObjectVariantsInfo();
    catalogObjectVariantsInfo.title = strings.VARIANTS_OBJECT_LABEL;
    catalogObjectVariantsInfo.options = catalogObject.getVariants().map(
      (variant) => optionsConstructor(variant),
    );

    setCatalogObjectControllerData(catalogObjectInfo);
  }, []);

  const applyChangeChoiceRules = (selections : TreeOptionSelections, rules : ModelRuleData[]) : TreeOptionSelections => {
    const rulesSelection = selections;
    let newSelection : TreeOptionSelection | undefined;

    for (let i = 0; i < currentOptionSelection.length; i++) {
      for (let n = 0; n < selections.length; n++) {
        if (currentOptionSelection[i].groupID === selections[n].groupID) {
          if (currentOptionSelection[i].optionID !== selections[n].optionID) {
            newSelection = currentOptionSelection[i];
          }

          break;
        }
      }
    }

    if (!newSelection) {
      return rulesSelection;
    }

    const selectionRules = rules.filter((r) => r.type === 'filtering' || r.type === 'subvariants');

    if (selectionRules.length === 0) {
      return rulesSelection;
    }

    selections.forEach((selection, index) => {
      rules.forEach((rule) => {
        rule.ruleDataFilter?.conditions.forEach((condition) => {
          if (selection.groupID === condition.groupID && newSelection?.groupID !== selection.groupID) {
            const action = rule.ruleDataFilter?.actions.find((a) => a.groupID === selection.groupID);
            if (action && action.options.length > 0) {
              const correspondingOption = action.options.find((optionId) => optionId === selection.optionID);
              if (correspondingOption) {
                rulesSelection[index].optionID = correspondingOption;
              } else {
                rulesSelection[index].optionID = action.options[0];
              }
            }
          }
        });
      });
    });

    setCurrentOptionSelection(rulesSelection);

    return rulesSelection;
  };

  const changeCustomAttributesHandler = (attributes: CustomAttributeValues) => {

    setCurrentCustomAttrubutesValues([...attributes]);

    if (catalogObjectControllerData === undefined) {
      return;
    }
    const newCatalogObjectControllerData = CatalogObjectControllerData.CreateFromCatalogObjectControllerData(
      catalogObjectControllerData,
      catalogObjectControllerData.variantComposition,
      catalogObjectControllerData.lastVariantSelected,
    );
    newCatalogObjectControllerData.customAttributesValues = [...attributes];
    setCatalogObjectControllerData(newCatalogObjectControllerData);
  };

  const changeComponent = (selections : TreeOptionSelections) => {

    if (currentOptionSelection.length === 0) {
      setCurrentOptionSelection(selections);
    }

    if (catalogObjectControllerData === undefined) {
      return;
    }

    // Find the collection
    const collection = findCollectionByName(collectionName)!;

    // Find the catalog object
    const catalogObject = catalog.getModelByCollectionIDAndModelName(collection.id, objectName)!;
    let selection = selections;

    if (catalogObject.catalogData?.rules) {
      selection = applyChangeChoiceRules(selections, catalogObject.catalogData?.rules);
    }

    // Try to deduce of this combination matches any known variant.
    // If so, grab the name, and pass it to the new object
    let deducedVariantName = '';
    if (catalogObjectControllerData.catalogModelData.data
        && catalogObjectControllerData.catalogModelData.data.variants) {

      deducedVariantName = POSCatalogModelData.GetVariantDescription(
        selection,
        catalogObjectControllerData.catalogModelData.data.variants,
      );
    }

    // eslint-disable-next-line max-len
    const newCatalogObjectControllerData = CatalogObjectControllerData.CreateFromCatalogObjectControllerData(
      catalogObjectControllerData,
      selection,
      deducedVariantName,
    );
    if (newCatalogObjectControllerData.catalogModelData.catalogData && newCatalogObjectControllerData.catalogModelData.catalogData.illumination === undefined) {
      console.log('Illumination data is undefined, cloning from catalog object', catalog.catalogFileInfo?.illuminationData);
      newCatalogObjectControllerData.catalogModelData.catalogData.illumination = _.cloneDeep(catalog.catalogFileInfo?.illuminationData);
    }
    setCatalogObjectControllerData(newCatalogObjectControllerData);
  };

  // const changeCustomization = (
  //   partIdentifier : string,
  //   customizationID : string,
  //   data : CustomizationValueDataImage | undefined,
  // ) => {

  //   if (catalogObjectControllerData === undefined) {
  //     return;
  //   }

  //   // eslint-disable-next-line max-len
  //   const customizationData : CustomizationValue[] = catalogObjectControllerData.customizationData;

  //   // Remove current customization for this item, if there
  //   const updateCustomizationData = customizationData.filter((value) => {
  //     return (
  //       CustomizationValue.getPartIdentifier(value) !== partIdentifier
  //       || CustomizationValue.getCustomizationID(value) !== customizationID
  //     );
  //   });

  //   if (data !== undefined) {
  //     // Add the new item
  //     updateCustomizationData.push(
  //       CustomizationValue.create(partIdentifier, customizationID, data),
  //     );
  //   }

  //   const newCatalogObjectControllerData = catalogObjectControllerData.CreateCopy();
  //   newCatalogObjectControllerData.customizationData = [...updateCustomizationData];

  //   setCatalogObjectControllerData(newCatalogObjectControllerData);
  // };

  // const getPartCustomizationsData = () : CustomizationValue[] => {

  //   if (catalogObjectControllerData === undefined) {
  //     return [];
  //   }

  //   const { customizationData } = catalogObjectControllerData;

  //   return customizationData.filter((value) => {
  //     return CustomizationValue.getPartIdentifier(value) === selectedPartID;
  //   });
  // };

  const getObjectCustomizations = () => {
    // Get the collection
    const collection = findCollectionByName(collectionName)!;

    // Find the catalog object
    const catalogObject = catalog.getModelByCollectionIDAndModelName(collection.id, objectName)!;

    // Grab the customizations
    return catalogObject.getCustomizations();
  };

  // const getObjectCustomizationsForGeometry = (geometryID : string) => {
  //   // Check if the object is valid
  //   if (!geometryID) {
  //     return [];
  //   }

  //   // Grab the object customizations
  //   const customizationConfiguration = getObjectCustomizations();
  //   if (customizationConfiguration === undefined) {
  //     return [];
  //   }

  //   // In this configuration, check if this object is mentioned
  //   const arrCustomizationObjects = CustomizationConfiguration.getArrObjects(
  //     customizationConfiguration,
  //   );
  //   if (!arrCustomizationObjects) {
  //     return [];
  //   }

  //   const customizationObject = arrCustomizationObjects.find(
  //     (co) => {
  //       return (
  //         CustomizationObject.getMeshID(co) === geometryID
  //       );
  //     },
  //   );

  //   if (!customizationObject) {
  //     return [];
  //   }

  //   // Grab the customizationsIDs used by this object

  //   const arrObjectCustomizationIDs = CustomizationObject.getArrCustomizationsIDs(
  //     customizationObject,
  //   );

  //   if (!arrObjectCustomizationIDs || arrObjectCustomizationIDs.length === 0) {
  //     return [];
  //   }

  //   // Using the Customization IDs, grab the customizations definitions with the specified IDs

  //   const arrAllCustomizationsDefinitions = CustomizationConfiguration.getArrCustomizations(
  //     customizationConfiguration,
  //   );

  //   const arrObjectCustomizations = arrAllCustomizationsDefinitions.filter(
  //     (customizationDefinition) => {
  //       return arrObjectCustomizationIDs.includes(
  //         Customization.getID(customizationDefinition),
  //       );
  //     },
  //   );

  //   if (arrObjectCustomizations.length !== arrObjectCustomizationIDs.length) {
  //     // eslint-disable-next-line no-console
  //     console.log(
  //       'Something weird happened because not all configurations were found based on IDs',
  //     );
  //   }

  //   return arrObjectCustomizations;
  // };

  // const handlePartSelected = (geometryID : string) => {
  //   setSelectedPartID(geometryID);
  // };

  useEffect(() => {

    // if (selectedPartID) {
    //   const objectPartCustomizations = getObjectCustomizationsForGeometry(selectedPartID);
    //   setSelectedPartCustomizations(objectPartCustomizations);
    // }

  }, [selectedPartID]);

  if (catalogObjectControllerData) {
    return (
      <ObjectView
        collectionName={collectionName}
        priceData={catalog.catalogFileInfo?.priceData}
        catalogObjectControllerData={catalogObjectControllerData}
        // variants={variants}
        measures={measures}
        customLineAttributes={catalog.catalogFileInfo?.itemsCustomAttributesDefinitions}
        objectCustomizations={getObjectCustomizations()}
        objectCustomizationsData={catalogObjectControllerData.customizationData}
        changeComponentHandler={changeComponent}
        changeCustomAttributesHandler={changeCustomAttributesHandler}
        configurations={configurations}
        tagsTranslation={catalog.catalogFileInfo?.tagsTranslation}
        // modelIllumination={modelIllumination}
      />
    );
  }

  return (<div className="empty" />);

}
